import { useState } from 'react';
import PropTypes from 'prop-types';
import { isoDay, previewEndsOn } from '../../../utils/contractPeriods';

/**
 * Add or edit one contract period (TD-2812).
 *
 * A period is entered the way a contract is signed: a start date, a length in
 * months, and free months granted at the start. Free months EXTEND the period —
 * 12 paid plus 3 free is one 15-month term carrying the year's allowance, not a
 * 12-month term with a tail — so the end date is derived rather than typed, and
 * shown so nobody has to take that on trust.
 */

const LIMIT_FIELDS = [
  { key: 'profiler_download_limit', label: 'Download limit' },
  { key: 'agent_token_limit', label: 'Token limit (input + output)' },
  { key: 'agent_weekly_token_limit', label: 'Weekly token limit' },
  { key: 'enrichment_credit_limit', label: 'Contact credit limit' },
  { key: 'max_concurrent_users', label: 'Max concurrent users' }
];

const toInput = (value) => (value === null || value === undefined ? '' : String(value));

// Whole numbers only: the backend refuses fractions, and half a free month
// would be stored without ever moving the end date.
const isWholeAtLeast = (value, min) => {
  const number = Number(value);
  return value !== '' && Number.isInteger(number) && number >= min;
};

const PeriodForm = ({ period, isSaving, onCancel, onSubmit }) => {
  const isEdit = !!period?.id;

  const [startsOn, setStartsOn] = useState(isoDay(period?.starts_on));
  const [lengthMonths, setLengthMonths] = useState(isEdit ? '' : '12');
  const [freeMonths, setFreeMonths] = useState(toInput(period?.free_months ?? 0));
  const [label, setLabel] = useState(period?.label || '');
  const [endsOn, setEndsOn] = useState(isoDay(period?.ends_on));
  const [limits, setLimits] = useState(() =>
    LIMIT_FIELDS.reduce((acc, field) => ({ ...acc, [field.key]: toInput(period?.[field.key]) }), {})
  );
  const [validationError, setValidationError] = useState(null);

  // An explicit end date wins; otherwise the length derives it. Editing an
  // existing period leaves the length blank, so its stored end date stands
  // until someone types a new length.
  const derived = previewEndsOn(startsOn, lengthMonths, freeMonths);
  const effectiveEnd = derived || endsOn;

  const setLimit = (key, value) => setLimits((prev) => ({ ...prev, [key]: value }));

  const validate = () => {
    if (!startsOn) return 'A start date is required';
    // Empty length + empty end is an open-ended period (ends_on: null). The End
    // field hint says so for create and edit alike; a create-only "length or
    // end required" guard contradicted that and blocked the path edit already
    // allowed.
    if (effectiveEnd && effectiveEnd < startsOn) {
      return 'The end date must not be before the start date';
    }
    if (freeMonths !== '' && !isWholeAtLeast(freeMonths, 0)) {
      return 'Free months must be a whole number of 0 or more';
    }
    if (lengthMonths !== '' && !isWholeAtLeast(lengthMonths, 1)) {
      return 'Length must be a whole number of months, 1 or more';
    }
    const invalid = LIMIT_FIELDS.find(
      (field) => limits[field.key] !== '' && !isWholeAtLeast(limits[field.key], 0)
    );
    if (invalid) return `${invalid.label} must be a whole number of 0 or more, or empty`;
    return null;
  };

  const handleSubmit = () => {
    const error = validate();
    setValidationError(error);
    if (error) return;

    onSubmit({
      starts_on: startsOn,
      // Sent as an explicit end date rather than as a length, so what the admin
      // was shown in the preview is exactly what is stored.
      ends_on: effectiveEnd || null,
      free_months: Number(freeMonths) || 0,
      label: label.trim() || null,
      ...LIMIT_FIELDS.reduce(
        (acc, field) => ({
          ...acc,
          // Blank means "unchanged from the period before", which is not the
          // same as zero and is why an empty field sends null.
          [field.key]: limits[field.key] === '' ? null : Number(limits[field.key])
        }),
        {}
      )
    });
  };

  return (
    <div className="contract-period-form">
      <h5>{isEdit ? `Edit period ${period.seq}` : 'Add period'}</h5>

      {validationError && (
        <div className="subscription-form-validation-error" role="alert">
          {validationError}
        </div>
      )}

      <div className="contract-period-form-row">
        <div className="subscription-form-group">
          <label htmlFor="period-starts-on">
            Start{' '}
            <span className="required-indicator" aria-hidden="true">
              *
            </span>
          </label>
          <input
            id="period-starts-on"
            type="date"
            value={startsOn}
            onChange={(e) => setStartsOn(e.target.value)}
          />
        </div>

        <div className="subscription-form-group">
          <label htmlFor="period-length-months">Length (months)</label>
          <input
            id="period-length-months"
            type="number"
            min="1"
            step="1"
            value={lengthMonths}
            onChange={(e) => setLengthMonths(e.target.value)}
            placeholder={isEdit ? 'Unchanged' : '12'}
          />
        </div>

        <div className="subscription-form-group">
          <label htmlFor="period-free-months">
            Free months{' '}
            <span className="subscription-form-hint">– granted at the start, extend the term</span>
          </label>
          <input
            id="period-free-months"
            type="number"
            min="0"
            step="1"
            value={freeMonths}
            onChange={(e) => setFreeMonths(e.target.value)}
          />
        </div>
      </div>

      <div className="contract-period-form-row">
        <div className="subscription-form-group">
          <label htmlFor="period-ends-on">End</label>
          <input
            id="period-ends-on"
            type="date"
            value={effectiveEnd}
            onChange={(e) => {
              setEndsOn(e.target.value);
              setLengthMonths('');
            }}
          />
          <span className="subscription-form-hint">
            {derived
              ? `Derived from the length: ${effectiveEnd}. Clear the length to set it by hand.`
              : 'Leave empty for an open-ended period. That also clears the contract expiry: the customer is never marked expired, and their revealed contact data is never deleted.'}
          </span>
        </div>

        <div className="subscription-form-group">
          <label htmlFor="period-label">Label</label>
          <input
            id="period-label"
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Year 1 (incl. 3 free months)"
          />
        </div>
      </div>

      <div className="contract-period-form-limits">
        {LIMIT_FIELDS.map((field) => (
          <div className="subscription-form-group" key={field.key}>
            <label htmlFor={`period-${field.key}`}>{field.label}</label>
            <input
              id={`period-${field.key}`}
              type="number"
              min="0"
              step="1"
              value={limits[field.key]}
              onChange={(e) => setLimit(field.key, e.target.value)}
              placeholder="Unchanged"
            />
          </div>
        ))}
      </div>

      <div className="subscription-form-actions">
        <button className="subscription-btn-cancel" onClick={onCancel} type="button">
          Cancel
        </button>
        <button
          className="subscription-btn-save"
          onClick={handleSubmit}
          disabled={isSaving}
          type="button"
        >
          {isSaving ? 'Saving...' : 'Save period'}
        </button>
      </div>
    </div>
  );
};

PeriodForm.propTypes = {
  period: PropTypes.object,
  isSaving: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
};

export default PeriodForm;
